import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from 'react'
import {
    CompositeImage,
    INotificationFieldSuggestions,
    IPageAnalysisError,
} from '../../../interfaces/notification-field-suggestions'
import { NotificationContentModel } from '../../../models/notification/notification-content.model'
import { DeliveryChannel } from '@pushly/models/lib/enums'
import { useSharedChannelFieldSetter } from './use-shared-channel-field-setter'
import { IEnhancedNotificationFieldSuggestions } from '../elements/field-suggestion-results-manager/types'

type FieldSuggestionsResults = {
    readonly source: 'page_analyzer' | 'pushly_ai'
    readonly data: IEnhancedNotificationFieldSuggestions[]
    readonly error?: IPageAnalysisError | null
    readonly currentVariantIndex?: number
}

type FieldSuggestionResultsManager = {
    localFieldSuggestionResults: FieldSuggestionsResults | null
    // Setter accepts INotificationFieldSuggestions[] and transforms to IEnhancedNotificationFieldSuggestions[] internally
    setLocalFieldSuggestionResults: (
        value: SetStateAction<{
            source: 'page_analyzer' | 'pushly_ai'
            data: INotificationFieldSuggestions[]
            error?: IPageAnalysisError | null
            currentVariantIndex?: number
        } | null>,
    ) => void
    applyFieldSuggestionResults: (
        fieldSuggestionResults: INotificationFieldSuggestions | Partial<INotificationFieldSuggestions>,
        options: {
            skipOnChangeHandler?: boolean
            source: 'page_analyzer' | 'pushly_ai'
            replaceKeywords?: boolean
            generationId?: string | null
            variantIndex?: number
            url?: string
        },
    ) => NotificationContentModel | null
    suggestedImageComposite: CompositeImage | undefined
    setSuggestedImageComposite: Dispatch<SetStateAction<CompositeImage | undefined>>
}

export const useFieldSuggestionResultsManager = (
    value: NotificationContentModel,
    channels: DeliveryChannel[] = [],
    onChange: (value: NotificationContentModel) => void,
): FieldSuggestionResultsManager => {
    const [suggestedImageComposite, setSuggestedImageComposite] = useState<CompositeImage | undefined>(undefined)
    const [localFieldSuggestionResults, setLocalFieldSuggestionResults] = useState<{
        source: 'page_analyzer' | 'pushly_ai'
        data: IEnhancedNotificationFieldSuggestions[]
        error?: IPageAnalysisError | null
        currentVariantIndex?: number
    } | null>(null)

    const setSharedChannelField = useSharedChannelFieldSetter(value, channels, onChange)

    // Keep `workingKw` referentially stable when the keyword *contents* are unchanged
    // (it's a dependency of the `applyFieldSuggestionResults` callback below, so a fresh
    // array each render would needlessly re-create that callback). We compare against the
    // PREVIOUS value held in a ref. The original code compared against `workingKw` itself
    // — its own binding — which was always `undefined` when the factory ran under es5 (so
    // this optimization never actually fired) and is a TDZ crash under es2020.
    const prevWorkingKw = useRef<string[]>([])
    const workingKw: string[] = useMemo(() => {
        const currKw = [...(value.getDefaultContent().getKeywords() ?? [])]
        const prev = prevWorkingKw.current
        const kwAreSame = prev.length === currKw.length && prev.every((kw) => currKw.includes(kw))
        const result = kwAreSame ? prev : currKw
        prevWorkingKw.current = result
        return result
    }, [value.getDefaultContent().getKeywords()])

    /**
     * Helper function to transform INotificationFieldSuggestions to IEnhancedNotificationFieldSuggestions
     * Flattens sourceMetadata.tone and sourceMetadata.reason to top-level properties for backward compatibility
     */
    const enhanceFieldSuggestions = (
        suggestions: INotificationFieldSuggestions | Partial<INotificationFieldSuggestions>,
    ): IEnhancedNotificationFieldSuggestions => {
        return {
            ...suggestions,
            // Flatten sourceMetadata to top-level for backward compatibility
            tone: suggestions.sourceMetadata?.tone ?? suggestions.tone,
            reason: suggestions.sourceMetadata?.reason ?? suggestions.reason,
        } as IEnhancedNotificationFieldSuggestions
    }

    const applyFieldSuggestionResults = useCallback(
        (
            fieldSuggestionResults: INotificationFieldSuggestions | Partial<INotificationFieldSuggestions>,
            options: {
                skipOnChangeHandler?: boolean
                source: 'page_analyzer' | 'pushly_ai'
                replaceKeywords?: boolean
                generationId?: string | null
                variantIndex?: number
                url?: string
            },
        ) => {
            const skipDispatch = options.skipOnChangeHandler ?? false

            let update = value.clone()

            const hasTitle = !!fieldSuggestionResults.title
            const hasBody = !!fieldSuggestionResults.body
            const hasImage = !!fieldSuggestionResults.imageUrl
            const hasKeywords = !!fieldSuggestionResults.keywords

            const hasSelections = hasTitle || hasBody || hasImage || hasKeywords
            if (!hasSelections) {
                setLocalFieldSuggestionResults(null)
                return null
            }

            // Persist AI generation metadata to notification content
            // This includes the source (which AI tool generated the content) and
            // optional metadata (tone, reason) that provides context about the generation

            // Extract tone and reason from either top-level (IEnhancedNotificationFieldSuggestions)
            // or from sourceMetadata (INotificationFieldSuggestions)
            const tone = fieldSuggestionResults.tone ?? fieldSuggestionResults.sourceMetadata?.tone
            const reason = fieldSuggestionResults.reason ?? fieldSuggestionResults.sourceMetadata?.reason

            // Build sourceMetadata based on source type.
            // Uses !== null && !== undefined checks (not truthy) to preserve valid
            // falsy values like variant_index 0 and empty-string edge cases.
            let sourceMetadata: Record<string, any> | null = null
            if (options.source === 'pushly_ai') {
                // For pushly_ai: include request_id, variant_index, tone, and reason
                sourceMetadata = {
                    ...(options.generationId !== null && options.generationId !== undefined
                        ? { request_id: options.generationId }
                        : {}),
                    ...(options.variantIndex !== null && options.variantIndex !== undefined
                        ? { variant_index: options.variantIndex }
                        : {}),
                    ...(tone !== null && tone !== undefined ? { tone } : {}),
                    ...(reason !== null && reason !== undefined ? { reason } : {}),
                }
                // Only set to null if no metadata fields are present
                if (Object.keys(sourceMetadata).length === 0) sourceMetadata = null
            } else if (options.source === 'page_analyzer') {
                // For page_analyzer: include url
                sourceMetadata = options.url !== null && options.url !== undefined ? { url: options.url } : null
            }

            update.getDefaultContent().setAutoSuggestedFieldsResults({
                source: options.source,
                sourceMetadata,
                title: fieldSuggestionResults.title ?? null,
                body: fieldSuggestionResults.body ?? null,
                imageUrl: fieldSuggestionResults.imageUrl ?? null,
                compositeImage: fieldSuggestionResults.compositeImage ?? null,
                keywords: fieldSuggestionResults.keywords ?? null,
            })

            if (hasTitle) {
                update = setSharedChannelField('Title', fieldSuggestionResults.title!, {
                    prevValue: update,
                    waitToCompleteChange: true,
                })
            }

            if (hasBody) {
                update = setSharedChannelField('Body', fieldSuggestionResults.body!, {
                    prevValue: update,
                    waitToCompleteChange: true,
                })
            }

            if (hasImage) {
                update = setSharedChannelField('ImageUrl', fieldSuggestionResults.imageUrl!, {
                    prevValue: update,
                    waitToCompleteChange: true,
                })
                if (fieldSuggestionResults.compositeImage) {
                    setSuggestedImageComposite(fieldSuggestionResults.compositeImage)
                }
            }

            if (hasKeywords) {
                const compiledKws = options.replaceKeywords
                    ? fieldSuggestionResults.keywords!
                    : Array.from(new Set([...workingKw, ...(fieldSuggestionResults.keywords ?? [])]))
                update.getDefaultContent().setKeywords(compiledKws)
            }

            if (!skipDispatch) {
                onChange?.(update)
            }
            setLocalFieldSuggestionResults(null)

            return update
        },
        [value, workingKw, onChange],
    )

    const wrappedSetLocalFieldSuggestionResults = useCallback(
        (
            setValue: SetStateAction<{
                source: 'page_analyzer' | 'pushly_ai'
                data: INotificationFieldSuggestions[]
                currentVariantIndex?: number
            } | null>,
        ) => {
            if (typeof setValue === 'function') {
                setLocalFieldSuggestionResults((prev) => {
                    const prevInput =
                        prev === null
                            ? null
                            : {
                                  ...prev,
                                  data: prev.data as INotificationFieldSuggestions[],
                              }
                    const newValue = setValue(prevInput)
                    if (newValue === null) return null
                    return {
                        ...newValue,
                        data: newValue.data.map(enhanceFieldSuggestions),
                    }
                })
            } else if (setValue === null) {
                setLocalFieldSuggestionResults(null)
            } else {
                setLocalFieldSuggestionResults({
                    ...setValue,
                    data: setValue.data.map(enhanceFieldSuggestions),
                })
            }
        },
        [],
    )

    return useMemo(
        () => ({
            localFieldSuggestionResults,
            setLocalFieldSuggestionResults: wrappedSetLocalFieldSuggestionResults,
            applyFieldSuggestionResults,
            suggestedImageComposite,
            setSuggestedImageComposite,
        }),
        [
            localFieldSuggestionResults,
            wrappedSetLocalFieldSuggestionResults,
            applyFieldSuggestionResults,
            suggestedImageComposite,
        ],
    )
}
